/* eslint-disable jira/filename-case */
/**
 * Module to work with Atlassian Resource Identifier (ARI)
 * More info in go/aris
 */

export type Ari = string;

export type AriConfig = {
	resourceOwner: string;
	cloudId?: string;
	resourceType: string;
	workspaceId?: string;
	resourceId: string;
	activationId?: string;
};

const ARI_REG_EXP =
	/^ari:cloud:(?<resourceOwner>[a-z.-]+):(?<cloudId>[\w-]*):(?<resourceType>[a-z.-]+)\/(?<resourceId>[\w.~_-]+)$/;
const ARI_REG_EXP_ACTIVATION =
	/^ari:cloud:(?<resourceOwner>[a-z.-]+):(?<cloudId>[\w-]*):(?<resourceType>[a-z.-]+)\/activation\/(?<activationId>[\w-]*)\/(?<resourceId>[\w.~_-]+)$/;
const ARI_REG_EXP_WORKSPACE =
	/^ari:cloud:(?<resourceOwner>[a-z.-]+):(?<cloudId>.{0}):(?<resourceType>[a-z.-]+)(\/(?<workspaceId>[\w.~_-]+)?)\/(?<resourceId>[\w.~_-]+)$/;
const ARI_REG_EXP_PROVIDER_CONTAINER =
	/^ari:cloud:(?<resourceOwner>[a-z.-]+):(?<cloudId>[\w-]*):(?<resourceType>[a-z.-]+)\/activation\/(?<activationId>[\w-]*)\/(?<providerAppId>[\w.~_-]+)\/(?<resourceId>[\w.~_/-]+)$/;

/**
 * Returns true if string is a valid ARI.
 * NOTE: Current implementation does not support all the different ARI formats/shapes that exist in the [ARI Registry](https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry), we will need a match more advanced parsing to support all of them.
 */
export const isAri = (ari: string): boolean =>
	ARI_REG_EXP.test(ari) ||
	ARI_REG_EXP_WORKSPACE.test(ari) ||
	ARI_REG_EXP_ACTIVATION.test(ari) ||
	ARI_REG_EXP_PROVIDER_CONTAINER.test(ari);

/**
 * Generates ARI from the config.
 * Prefer getting ARIs from the backend over using this function and constructing them in code.
 * Use this funciton only if you cannot get ARI from the backend, e.g. if you need to craete and ARI from projectId in URL.
 */
export const createAri = ({
	resourceOwner,
	cloudId,
	resourceType,
	workspaceId,
	resourceId,
	activationId,
}: AriConfig): Ari => {
	if (workspaceId != null) {
		return `ari:cloud:${resourceOwner}::${resourceType}/${workspaceId}/${resourceId}`;
	}

	if (activationId != null) {
		return `ari:cloud:${resourceOwner}:${
			cloudId || ''
		}:${resourceType}/activation/${activationId}/${resourceId}`;
	}

	return `ari:cloud:${resourceOwner}:${cloudId || ''}:${resourceType}/${resourceId}`;
};

/**
 * Validates ARI from string value
 */
export const toAri = (ari: string): Ari | undefined => {
	if (isAri(ari)) {
		return ari;
	}

	return undefined;
};

export const toAriOrDefault = (ari: string, defaultValue: Ari | string = ''): Ari =>
	toAri(ari) ?? defaultValue;

/**
 * Returns ARI config from ari string.
 *
 * @deprecated Use `getAriConfig` instead. The argument is a pre-validated `Ari` type,
 *             meaning it doesn't need to be re-validated (and hence getting the config
 *             from it cannot reasonably fail).
 */
export const parseAri = (ari: Ari): AriConfig | undefined => {
	if (isAri(ari)) {
		const parts =
			ARI_REG_EXP.exec(ari) ||
			ARI_REG_EXP_WORKSPACE.exec(ari) ||
			ARI_REG_EXP_ACTIVATION.exec(ari) ||
			ARI_REG_EXP_PROVIDER_CONTAINER.exec(ari);

		if (parts?.groups) {
			// reconstructing an object just to avoid flow error (otherwise says inexact object type of parts.groups is incompatible with exact AriConfig type)
			const { resourceOwner, cloudId, resourceType, workspaceId, resourceId, activationId } =
				parts.groups;
			return {
				resourceOwner,
				cloudId,
				resourceType,
				workspaceId,
				resourceId,
				activationId,
			};
		}
	}

	return undefined;
};

/**
 * Returns ARI config from pre-validated `Ari` type.
 * Instead of parsing resourceId out of ARI - consider using ARI as the resource's id (e.g. for caching)
 * NOTE: both isAri() and parseAri() do not support all the different ARI shapes that exist in the [ARI Registry](https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry)
 */
export const getAriConfig = (ari: Ari): AriConfig => {
	const config = parseAri(ari);
	if (config === undefined) {
		throw Error('Invalid ARI - this should only be possible if there is a bug in `toAri`');
	}
	return config;
};
